import {
  faExclamationTriangle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import * as Config from '../../../public/external-links'
import Accordion from '../../common/Accordion'
import GreyBox from '../../common/GreyBox'
import PageTitle from '../../PageTitle'
import { ICallout } from '../../../models/Callout'
import useCallout from '../../../hooks/useCallout'
import LoaderContext from '../../context/FormLoader'
import Conditional from '../../common/Conditional'
import CalloutDisplay from '../../common/CalloutDisplay'
import DocumentLink from '../../common/DocumentLink'

export const WarningLetterHeader = (props) => {
  const mountedRef = useRef(false)
  const [data, setData] = useState<ICallout[]>([])
  const [getCallout] = useCallout('WarningLetterTemplate', 'Before')
  const [isLoading, setIsLoading] = useState(true)
  const loader = useContext(LoaderContext)

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    if (mountedRef.current) {
      getCallout()
        .then((response) => {
          if (mountedRef.current) {
            setData(response.callouts)
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setIsLoading(false)
          loader.down()
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  return (
    <>
      <PageTitle title="Warning letter - Templates - Fair Work Ombudsman" />
      <h1>Warning letter</h1>
      <Accordion
        heading="Welcome to our Template Tool"
        expanded={props.show}
        onClick={() => props.setShow()}
      >
        <p></p>
        <Conditional if={!isLoading}>
          {data &&
            data.map((c, i) => (
              <CalloutDisplay
                key={i}
                className=""
                content={c.content}
                name="WarningLetter"
              />
            ))}
        </Conditional>
        <p></p>
        <p>
          This tool will help you prepare a warning letter or a record of a
          formal performance meeting to give to an employee who is having
          ongoing performance problems.
        </p>
        <p>
          The tool will ask you simple questions and use your answers to tailor
          a letter for you. You will be able to save and edit your letter at the
          end.
        </p>
        <h3>Important information</h3>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faExclamationTriangle}
            size={'1x'}
            color={'#da291c'}
          />
          &nbsp;buttons for important information about workplace rights and
          obligations
        </p>
        <h2>Get help</h2>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faInfoCircle}
            size={'lg'}
            color={'#1b365d'}
          />
          &nbsp;buttons for explanations or help answering the question
        </p>
        <p>
          To protect your privacy none of the information you enter is stored in
          this tool or captured by the Office of the Fair Work Ombudsman.
        </p>

        <GreyBox>
          <p>
            <strong>Before you begin:</strong> To prepare your letter
            you&apos;ll need to know if there has been a performance discussion
            with the employee, what was discussed at this meeting and whether
            any previous formal warnings have been issued to this employee.
          </p>
          <p>All fields are required unless marked optional.</p>
        </GreyBox>

        <Button className="ui is-primary button" onClick={props.handleStart}>
          Start
        </Button>

        <p>
          If you do not wish to use this tool, you can download and complete one
          of the following blank template letters:
        </p>
        <ul>
          <li>
            <DocumentLink
              name="First warning letter"
              urlDoc={Config.INTERACTIVE_TEMPLATE_FIRST_WARNING_LETTER_DOCX}
              urlPdf={Config.INTERACTIVE_TEMPLATE_FIRST_WARNING_LETTER_PDF}
              sizeDoc=" 46.9KB"
              sizePdf=" 136.2KB"
            />
            .
          </li>
          <li>
            <DocumentLink
              name="Final warning letter"
              urlDoc={Config.INTERACTIVE_TEMPLATE_FINAL_WARNING_LETTER_DOCX}
              urlPdf={Config.INTERACTIVE_TEMPLATE_FINAL_WARNING_LETTER_PDF}
              sizeDoc=" 50.7KB"
              sizePdf=" 47.7KB"
            />
            .
          </li>
          <li>
            <DocumentLink
              name="Staff meeting records template"
              urlDoc={
                Config.INTERACTIVE_TEMPLATE_STAFF_MEETING_RECORDS_TEMPLATE_DOCX
              }
              urlPdf={
                Config.INTERACTIVE_TEMPLATE_STAFF_MEETING_RECORDS_TEMPLATE_PDF
              }
              sizeDoc=" 59KB"
              sizePdf=" 118KB"
            />
            .
          </li>
        </ul>
      </Accordion>
    </>
  )
}
